// ============================================================
// WHAT A BUNDLE DOES - the bench's pinned band (plan armoury, part 6):
// each effect as a sentence, and what it changes on the sheet
// ("Strength 40 → 50", "Gains Frenzy").
//
// The sheet's changes are worked out through the effective sheet itself,
// with ONLY these effects on (an Always on item, a Solo mode, or the
// always-on list), against the sheet with nothing on - so the preview is
// what play will do, never a second reading of the rules. A trigger is
// previewed as if its condition held. A conditional sheet effect shows in
// the sheet's changes only when its condition holds for the combatant as
// they stand.
//
// Pure. Zero React dependency.
// ============================================================
import { CHARACTERISTICS } from '../character/characteristicList';
import { effectSentence, readEffects, type StanceEffect } from '../character/effectItems';
import { TALENT_CATALOG } from '../character/talentCatalog';
import { TRAIT_CATALOG } from '../character/traitCatalog';
import type { Character } from '../character/types';
import { effectiveSheet } from './stancedSheet';
import type { Combatant } from './types';

export interface StancePreview {
  says: string[];
  sheet: string[];
}

type Holder = Pick<Combatant, 'sheet' | 'state'>;

/** The sheet with nothing on it: no Always on, no Solo modes, no always-on list. */
function bare(sheet: Character): Character {
  const { alwaysOn: _a, soloModes: _s, ...rest } = sheet;
  return { ...rest, permanentEffects: [] };
}

const named = (catalog: Record<string, { name: string }>, id: string) => catalog[id]?.name ?? id;

/** What changed from one sheet to the other, in words. */
function changes(before: Character, after: Character): string[] {
  const out = CHARACTERISTICS.filter(
    (ch) => before.characteristics[ch.id].value !== after.characteristics[ch.id].value,
  ).map((ch) => `${ch.name} ${before.characteristics[ch.id].value} → ${after.characteristics[ch.id].value}`);
  if (before.wounds.max !== after.wounds.max) out.push(`Wounds ${before.wounds.max} → ${after.wounds.max}`);
  const talents = after.talents.slice(before.talents.length).map((t) => `Gains ${named(TALENT_CATALOG, t.talentId)}`);
  const traits = after.traits.slice(before.traits.length).map((t) => `Gains ${named(TRAIT_CATALOG, t.traitId)}`);
  return [...out, ...talents, ...traits];
}

/**
 * A bundle's sentences and its changes to the sheet: its effects as if up (a trigger needs no condition), on a sheet
 * with nothing else on it. A NAMED bundle is put up as an Always on item of that name, so a clause asking for it
 * (`own_stance`) holds as it will in play; the unnamed always-on list rides `permanentEffects`.
 */
export function stancePreview(c: Holder, effects: readonly StanceEffect[], name?: string): StancePreview {
  const before = bare(c.sheet);
  const read = readEffects(effects);
  const on = name ? { alwaysOn: [{ id: 'preview', name, effects: read }] } : { permanentEffects: read };
  return {
    says: read.map(effectSentence),
    sheet: changes(before, effectiveSheet({ sheet: { ...before, ...on }, state: c.state })),
  };
}
